/**
 * Evaluation Framework Module
 * 
 * Provides evaluation framework management functionality including CRUD operations,
 * global framework cloning, and tenant-specific framework customization.
 * Implements requirements FREC01-05 from the requirements document.
 */
import BaseModule, { NavigationItem, RouteItem, DashboardWidget, AnalyticsData, HelpContent } from './BaseModule';
import { Assessment } from '@mui/icons-material';
import EvaluationSchemaList from './evaluation_schemas/EvaluationSchemaList';
import EvaluationSchemaForm from './evaluation_schemas/EvaluationSchemaForm';
import EvaluationSchemaDetails from './evaluation_schemas/EvaluationSchemaDetails';
import GlobalSchemaLibrary from './evaluation_schemas/GlobalSchemaLibrary';
import React, { ReactNode } from 'react';

export class EvaluationSchemaModule extends BaseModule {
    constructor() {
        super();
        this.id = 'evaluationSchemas';
        this.name = 'Evaluation Framework Management';
        this.description = 'Manage evaluation frameworks with categories, criteria, and rating scales';
        this.version = '1.0.0';
        this.enabled = true;
    }

    /**
     * Get navigation items for the sidebar
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {Array} Array of navigation items
     */
    getNavigationItems(user: any, userProfile: any): NavigationItem[] {
        const items: NavigationItem[] = [];

        // All users can view evaluation schemas
        items.push({
            name: 'Evaluation Frameworks',
            icon: <Assessment />,
            path: '/evaluation-schemas',
            module: this.id
        });

        return items;
    }

    /**
     * Get React routes for the module
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {Array} Array of route objects
     */
    getRoutes(user: any, userProfile: any): RouteItem[] {
        return [
            {
                path: '/evaluation-schemas',
                element: <EvaluationSchemaList />,
                module: this.id
            },
            {
                path: '/evaluation-schemas/new',
                element: <EvaluationSchemaForm />,
                module: this.id
            },
            {
                path: '/evaluation-schemas/:id',
                element: <EvaluationSchemaDetails />,
                module: this.id
            },
            {
                path: '/evaluation-schemas/:id/edit',
                element: <EvaluationSchemaForm />,
                module: this.id
            },
            {
                path: '/evaluation-schemas/global-library',
                element: <GlobalSchemaLibrary />,
                module: this.id
            },
            {
                path: '/evaluation-schemas/global/:id',
                element: <EvaluationSchemaDetails />,
                module: this.id
            },
            {
                path: '/evaluation-schemas/global/:id/edit',
                element: <EvaluationSchemaForm />,
                module: this.id
            }
        ];
    }

    /**
     * Get dashboard widgets for the module
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {Array} Array of widget components
     */
    getDashboardWidgets(user: any, userProfile: any): DashboardWidget[] {
        // Widgets are placeholders for now as we don't have the implementation
        return [];
    }

    /**
     * Get analytics data for the module
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {Object} Analytics data object
     */
    async getAnalyticsData(user: any, userProfile: any): Promise<AnalyticsData> {
        return {
            totalSchemas: 0,
            activeSchemas: 0,
            globalSchemas: 0,
            tenantSchemas: 0,
            mostUsedSchema: null,
            schemaUsageStats: []
        };
    }

    /**
     * Get help content for the module
     * @returns {Object} Help content object
     */
    getHelpContent(user: any, userProfile: any): HelpContent {
        return {
            '/evaluation-schemas': {
                title: 'Evaluation Framework Management',
                description: 'Manage evaluation frameworks that define the rubric used to score presentations. Frameworks consist of categories (Presentation Structure, Soft Skills, Solution Knowledge) with evaluation criteria and rating scales.',
                quickTips: [
                    'Create frameworks with multiple categories for comprehensive evaluation',
                    'Use global frameworks as templates for consistent evaluation standards',
                    'Clone and customize global frameworks for tenant-specific needs',
                    'Assign frameworks to users based on their job roles',
                    'Set a default framework for new presenters in your tenant'
                ],
                faqs: [
                    {
                        question: 'What is an evaluation framework?',
                        answer: 'An evaluation framework is a structured rubric that defines how presentations are scored. It contains categories (like Presentation Structure, Soft Skills) and specific criteria with rating scales.'
                    },
                    {
                        question: 'Can I modify global frameworks?',
                        answer: 'No, global frameworks cannot be modified directly. However, you can clone them to create tenant-specific frameworks that you can then customize.'
                    },
                    {
                        question: 'How do I assign frameworks to users?',
                        answer: 'Tenant Administrators can assign specific evaluation frameworks to users based on their job roles (Account Manager, Sales Consultant, etc.) through the user management interface.'
                    }
                ]
            },
            '/evaluation-schemas/global-library': {
                title: 'Global Framework Library',
                description: 'Browse and clone global evaluation frameworks maintained by system administrators. These templates provide best-practice evaluation criteria.',
                quickTips: [
                    'Browse available global templates by category',
                    'Preview framework details before cloning',
                    'Clone frameworks to create customizable copies',
                    'Enable frameworks for your tenant for immediate use'
                ]
            },
            '/evaluation-schemas/global/:id': {
                title: 'Global Framework Details',
                description: 'View detailed information about global evaluation frameworks including categories, criteria, and rating scales.',
                quickTips: [
                    'Review framework structure and criteria',
                    'Clone framework to create a customizable copy',
                    'Global admins can edit global frameworks directly'
                ]
            }
        };
    }

    /**
     * Get Firestore collections used by the module
     * @returns {Array} Array of collection names
     */
    getCollections(): string[] {
        return [
            'evaluationSchemas',
            'globalEvaluationSchemas',
            'evaluationCategories',
            'evaluationCriteria'
        ];
    }

    /**
     * Get Firestore security rules for the module
     * @returns {string} Security rules string
     */
    getSecurityRules(): string {
        return `
      // Evaluation Framework security rules
      match /evaluationSchemas/{schemaId} {
        allow read: if request.auth != null && (
          resource.data.tenantId == request.auth.token.tenantId ||
          request.auth.token.role == 'globalAdmin'
        );
        allow create: if request.auth != null && (
          request.resource.data.tenantId == request.auth.token.tenantId &&
          (request.auth.token.role == 'tenantAdmin' || request.auth.token.role == 'globalAdmin')
        );
        allow update: if request.auth != null && (
          resource.data.tenantId == request.auth.token.tenantId &&
          (request.auth.token.role == 'tenantAdmin' || request.auth.token.role == 'globalAdmin')
        );
        allow delete: if request.auth != null && (
          resource.data.tenantId == request.auth.token.tenantId &&
          (request.auth.token.role == 'tenantAdmin' || request.auth.token.role == 'globalAdmin')
        );
      }

      // Global evaluation frameworks (read-only for tenants)
      match /globalEvaluationSchemas/{schemaId} {
        allow read: if request.auth != null;
        allow write: if request.auth != null && request.auth.token.role == 'globalAdmin';
      }

      // Evaluation categories and criteria
      match /evaluationCategories/{categoryId} {
        allow read: if request.auth != null && (
          resource.data.tenantId == request.auth.token.tenantId ||
          resource.data.isGlobal == true ||
          request.auth.token.role == 'globalAdmin'
        );
        allow write: if request.auth != null && (
          request.auth.token.role == 'tenantAdmin' || 
          request.auth.token.role == 'globalAdmin'
        );
      }

      match /evaluationCriteria/{criteriaId} {
        allow read: if request.auth != null && (
          resource.data.tenantId == request.auth.token.tenantId ||
          resource.data.isGlobal == true ||
          request.auth.token.role == 'globalAdmin'
        );
        allow write: if request.auth != null && (
          request.auth.token.role == 'tenantAdmin' || 
          request.auth.token.role == 'globalAdmin'
        );
      }
    `;
    }

    /**
     * Check if the module is available for the current user
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {boolean} Whether the module is available
     */
    isAvailable(user: any, userProfile: any): boolean {
        return this.enabled && user && userProfile;
    }
}

export default EvaluationSchemaModule;
